import { Link } from "react-router-dom"

const STEPS = [
  {
    label: "01 · Gather",
    title: "Bring everyone into the same room",
    body: "Bible Imposter is an in-person Bible party game for 3-8 players. Each person uses their own phone, so there are no printables, cards, pencils, or app-store downloads to prepare.",
  },
  {
    label: "02 · Peek",
    title: "Read your secret card",
    body: "Most players see the same Bible topic: a person, place, event, parable, verse, or object from Scripture. One player sees Imposter instead and has to bluff without knowing the topic.",
  },
  {
    label: "03 · Give clues",
    title: "Say one word out loud",
    body: "Players take turns giving one-word clues. Good clues prove you know the Bible topic without making it too easy for the imposter to copy the group.",
  },
  {
    label: "04 · Discuss",
    title: "Watch for the fake clue",
    body: "After everyone gives clues, the group talks through who sounded biblical, who sounded vague, and who might be pretending. The phone never records the clues; all discussion happens face to face.",
  },
  {
    label: "05 · Vote",
    title: "Accuse the imposter",
    body: "Everyone votes secretly on their phone. If the group catches the imposter, the group wins. If they accuse the wrong player or split the vote, the imposter wins.",
  },
]

const FAQS = [
  {
    question: "Is Bible Imposter free?",
    answer: "Yes. Bible Imposter is free to play in the browser.",
  },
  {
    question: "Is it good for youth group?",
    answer: "Yes. It works well as a Christian youth group game because it is quick, social, indoor-friendly, and does not require supplies.",
  },
  {
    question: "Can adults play it?",
    answer: "Yes. It is also a Bible party game for adults, small groups, church game nights, families, and Bible studies.",
  },
  {
    question: "Do we need to print anything?",
    answer: "No. The host creates a room, players join on their phones, and the game deals the secret Bible topic automatically.",
  },
  {
    question: "Is it trivia?",
    answer: "Not exactly. Bible Imposter is closer to a Bible guessing game or social deduction game: you use biblical knowledge to give clues and spot the player who is bluffing.",
  },
]

export function HowToPlayPage() {
  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
    >
      <header className="border-b-2 border-border px-5 pt-6 pb-5">
        <Link
          to="/"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Bible Imposter
        </Link>
        <h1 className="mt-4 font-display text-4xl uppercase leading-none tracking-tight text-foreground">
          How to play
        </h1>
        <p className="prose-body mt-4 text-base">
          Bible Imposter is a free Bible party game for groups, youth ministry, church game nights,
          Bible studies, and families. It is a no-print Christian game: one phone each, one hidden
          imposter, and one biblical topic per round.
        </p>
        <Link
          to="/create"
          data-slot="button"
          className="mt-5 flex h-14 items-center justify-center bg-primary px-4 text-base text-primary-foreground hover:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          Host a game
        </Link>
      </header>

      <section className="px-5 py-6">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          At a glance
        </h2>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="border-2 border-border bg-card p-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Players</dt>
            <dd className="mt-1 text-foreground">3-8</dd>
          </div>
          <div className="border-2 border-border bg-card p-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Time</dt>
            <dd className="mt-1 text-foreground">~10 min</dd>
          </div>
          <div className="border-2 border-border bg-card p-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Supplies</dt>
            <dd className="mt-1 text-foreground">Phones only</dd>
          </div>
          <div className="border-2 border-border bg-card p-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Best for</dt>
            <dd className="mt-1 text-foreground">Groups</dd>
          </div>
        </dl>
      </section>

      <section className="border-t-2 border-border px-5 py-6">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Rules
        </h2>
        <ol className="mt-4 flex flex-col gap-5">
          {STEPS.map((step) => (
            <li key={step.label}>
              <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {step.label}
              </span>
              <h3 className="mt-1 text-lg uppercase leading-tight tracking-tight text-foreground">
                {step.title}
              </h3>
              <p className="prose-body mt-2">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-t-2 border-border px-5 py-6">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Who it is for
        </h2>
        <p className="prose-body mt-4">
          Use Bible Imposter when you need an indoor Bible game for youth, a church party game for
          adults, a small-group icebreaker, or a quick family Bible game. It is built for people who
          want a fun biblical game without preparing printed cards or running a long lesson.
        </p>
      </section>

      <section className="border-t-2 border-border px-5 py-6">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          FAQ
        </h2>
        <div className="mt-4 flex flex-col gap-4">
          {FAQS.map((faq) => (
            <article key={faq.question}>
              <h3 className="text-base uppercase leading-tight tracking-tight text-foreground">
                {faq.question}
              </h3>
              <p className="prose-body mt-1">{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <footer className="border-t-2 border-border px-5 py-5">
        <Link to="/" className="text-sm text-foreground underline-offset-2 hover:underline">
          Back to Bible Imposter
        </Link>
      </footer>
    </main>
  )
}
